import { useEffect, useRef, useState } from 'react';
import { RecentBodyPhotoAssessmentsService } from '../services/RecentBodyPhotoAssessmentsService';
import {
  recentBodyPhotoAssessmentRowKey,
  type RecentBodyPhotoAssessmentsDashboard,
  type RecentBodyPhotoAssessmentsQuery,
} from '../utils/recentBodyPhotoAssessments';

const SEARCH_DEBOUNCE_MS = 350;

export function useRecentBodyPhotoAssessments(query: RecentBodyPhotoAssessmentsQuery | null) {
  const [snapshot, setSnapshot] = useState<RecentBodyPhotoAssessmentsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevSearch = useRef<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!query) {
      clearTimeout(debounceTimer.current);
      setSnapshot(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const onlySearchChanged = prevSearch.current !== null
      && prevSearch.current !== query.searchName;
    prevSearch.current = query.searchName;

    const run = () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);

      RecentBodyPhotoAssessmentsService.getDashboard(query)
        .then((next) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(next);
        })
        .catch((caught: unknown) => {
          if (currentRequest !== requestId.current) {
            return;
          }
          setSnapshot(null);
          setError(caught instanceof Error ? caught.message : 'Não foi possível carregar as fotos recentes.');
        })
        .finally(() => {
          if (currentRequest === requestId.current) {
            setLoading(false);
          }
        });
    };

    clearTimeout(debounceTimer.current);
    if (onlySearchChanged) {
      debounceTimer.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
    } else {
      run();
    }

    return () => clearTimeout(debounceTimer.current);
  }, [query]);

  const setReviewed = async (studentId: number, assessmentDate: string, reviewed: boolean) => {
    const rowKey = recentBodyPhotoAssessmentRowKey({ studentId, assessmentDate });
    const previous = snapshot;
    setSnapshot((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        assessments: current.assessments.map((row) => (
          recentBodyPhotoAssessmentRowKey(row) === rowKey
            ? {
              ...row,
              reviewed,
              reviewedAt: reviewed ? (row.reviewedAt ?? new Date().toISOString()) : null,
            }
            : row
        )),
      };
    });

    try {
      const saved = await RecentBodyPhotoAssessmentsService.setReviewed(
        studentId,
        assessmentDate,
        reviewed,
      );
      setSnapshot((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          assessments: current.assessments.map((row) => (
            recentBodyPhotoAssessmentRowKey(row) === rowKey
              ? { ...row, reviewed: saved.reviewed, reviewedAt: saved.reviewedAt }
              : row
          )),
        };
      });
    } catch (caught: unknown) {
      setSnapshot(previous);
      throw caught;
    }
  };

  return { snapshot, loading, error, setReviewed };
}
